import {
  getApiUrl,
  TENANT_BRANDING_APP_TITLE_STORAGE_KEY,
  TENANT_BRANDING_LOGO_URL_STORAGE_KEY,
  TENANT_BRANDING_NAME_STORAGE_KEY,
  TENANT_BRANDING_RAZA_PRINT_PAPER_SIZE_STORAGE_KEY,
  TENANT_BRANDING_THEME_STORAGE_KEY,
  TENANT_BRANDING_TRUST_ID_STORAGE_KEY,
  TENANT_BRANDING_TRUST_NAME_STORAGE_KEY,
} from "@/constants";
import httpClient from "@/dataprovider/http-client";
import {
  DEFAULT_RAZA_PRINT_PAPER_SIZE,
  resolveRazaPrintPaperSize,
  type RazaPrintPaperSize,
} from "./raza-print-paper-size";
import { applyStoredDocumentTitle } from "./apply-stored-document-title";
import { resolvePublicBrandingHostname } from "./public-tenant-branding-hostname";
import { parseTenantRetirement, storeTenantRetirement } from "./tenant-retirement";

export { applyStoredDocumentTitle };
export type { RazaPrintPaperSize };
export { DEFAULT_RAZA_PRINT_PAPER_SIZE, resolveRazaPrintPaperSize };

/** Fired after tenant branding is written to sessionStorage (login or refresh). */
export const TENANT_BRANDING_UPDATED_EVENT = "fakhri-tenant-branding-updated";

export type TenantBrandingIdentity = {
  tenantName: string;
  trustName: string;
  trustId: string;
  logoUrl: string;
};

const BRANDING_STORAGE_KEYS = [
  TENANT_BRANDING_THEME_STORAGE_KEY,
  TENANT_BRANDING_NAME_STORAGE_KEY,
  TENANT_BRANDING_APP_TITLE_STORAGE_KEY,
  TENANT_BRANDING_TRUST_NAME_STORAGE_KEY,
  TENANT_BRANDING_TRUST_ID_STORAGE_KEY,
  TENANT_BRANDING_LOGO_URL_STORAGE_KEY,
  TENANT_BRANDING_RAZA_PRINT_PAPER_SIZE_STORAGE_KEY,
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readOptionalString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed !== "" ? trimmed : undefined;
}

function storeOptionalString(key: string, value: string | undefined): void {
  if (value) {
    sessionStorage.setItem(key, value);
  } else {
    sessionStorage.removeItem(key);
  }
}

/** Stable fingerprint of all branding fields in sessionStorage. */
export function readBrandingStorageFingerprint(): string {
  return JSON.stringify({
    theme: sessionStorage.getItem(TENANT_BRANDING_THEME_STORAGE_KEY),
    tenantName: sessionStorage.getItem(TENANT_BRANDING_NAME_STORAGE_KEY),
    appTitle: sessionStorage.getItem(TENANT_BRANDING_APP_TITLE_STORAGE_KEY),
    trustName: sessionStorage.getItem(TENANT_BRANDING_TRUST_NAME_STORAGE_KEY),
    trustId: sessionStorage.getItem(TENANT_BRANDING_TRUST_ID_STORAGE_KEY),
    logoUrl: sessionStorage.getItem(TENANT_BRANDING_LOGO_URL_STORAGE_KEY),
    razaPrintPaperSize: sessionStorage.getItem(TENANT_BRANDING_RAZA_PRINT_PAPER_SIZE_STORAGE_KEY),
  });
}

function storeBrandingRow(first: Record<string, unknown>): unknown | undefined {
  if (!("themeOptions" in first)) return undefined;
  const before = readBrandingStorageFingerprint();
  const themeOptions = first.themeOptions ?? {};
  sessionStorage.setItem(TENANT_BRANDING_THEME_STORAGE_KEY, JSON.stringify(themeOptions));

  const tenantName = readOptionalString(first.tenantName);
  storeOptionalString(TENANT_BRANDING_NAME_STORAGE_KEY, tenantName);
  storeOptionalString(
    TENANT_BRANDING_APP_TITLE_STORAGE_KEY,
    readOptionalString(first.appTitle) ?? tenantName
  );
  storeOptionalString(TENANT_BRANDING_TRUST_NAME_STORAGE_KEY, readOptionalString(first.trustName));
  storeOptionalString(TENANT_BRANDING_TRUST_ID_STORAGE_KEY, readOptionalString(first.trustId));
  storeOptionalString(TENANT_BRANDING_LOGO_URL_STORAGE_KEY, readOptionalString(first.logoUrl));

  // Authenticated `/me` includes paper size; public hostname branding does not — leave cache alone.
  if ("razaPrintPaperSize" in first) {
    sessionStorage.setItem(
      TENANT_BRANDING_RAZA_PRINT_PAPER_SIZE_STORAGE_KEY,
      resolveRazaPrintPaperSize(
        typeof first.razaPrintPaperSize === "string" ? first.razaPrintPaperSize : null
      )
    );
  }

  // Authenticated `/me` always carries `retirement` (null unless the tenant is read-only here).
  if ("retirement" in first) {
    storeTenantRetirement(parseTenantRetirement(first.retirement));
  }

  applyStoredDocumentTitle();

  if (readBrandingStorageFingerprint() !== before) {
    notifyTenantBrandingUpdated();
  }
  return themeOptions;
}

function parseBrandingResponse(json: unknown): unknown | undefined {
  if (!isRecord(json)) return undefined;
  const rows = json.rows;
  if (!Array.isArray(rows) || rows.length === 0) return undefined;
  const first = rows[0];
  if (!isRecord(first)) return undefined;
  return storeBrandingRow(first);
}

let publicTenantBrandingInFlight: Promise<unknown | undefined> | null = null;
let tenantBrandingMeInFlight: Promise<unknown | undefined> | null = null;
let ensureTenantBrandingInFlight: Promise<unknown | undefined> | null = null;

const brandingListeners = new Set<() => void>();
let windowListenerAttached = false;

function notifyTenantBrandingUpdated(): void {
  window.dispatchEvent(new Event(TENANT_BRANDING_UPDATED_EVENT));
}

function attachWindowListener(): void {
  if (windowListenerAttached) return;
  windowListenerAttached = true;
  window.addEventListener(TENANT_BRANDING_UPDATED_EVENT, () => {
    brandingListeners.forEach((listener) => listener());
  });
}

/** Subscribe to branding cache updates. One shared window listener fans out to all subscribers. */
export function subscribeTenantBrandingUpdated(listener: () => void): () => void {
  attachWindowListener();
  brandingListeners.add(listener);
  return () => brandingListeners.delete(listener);
}

/** True when sessionStorage already has tenant theme options from a prior fetch this tab. */
export function hasStoredTenantBranding(): boolean {
  try {
    return sessionStorage.getItem(TENANT_BRANDING_THEME_STORAGE_KEY) != null;
  } catch {
    return false;
  }
}

/** Remove all tenant branding keys from sessionStorage (tenant switch / impersonation exit). */
export function clearStoredTenantBranding(): void {
  for (const key of BRANDING_STORAGE_KEYS) {
    sessionStorage.removeItem(key);
  }
  storeTenantRetirement(null);
}

/**
 * Hostname-based cosmetic branding (login / select-tenant before impersonation).
 * Dispatches {@link TENANT_BRANDING_UPDATED_EVENT} on success.
 */
export async function fetchAndStorePublicTenantBranding(): Promise<unknown | undefined> {
  if (publicTenantBrandingInFlight) {
    return publicTenantBrandingInFlight;
  }

  publicTenantBrandingInFlight = (async () => {
    try {
      const hostname = resolvePublicBrandingHostname({
        locationHostname: window.location.hostname,
        envHostname: import.meta.env.VITE_TENANT_HOSTNAME,
      });
      const url = hostname
        ? `${getApiUrl()}/tenant-branding/public?hostname=${encodeURIComponent(hostname)}`
        : `${getApiUrl()}/tenant-branding/public`;
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) return undefined;
      const json = (await response.json()) as unknown;
      return parseBrandingResponse(json);
    } finally {
      publicTenantBrandingInFlight = null;
    }
  })();

  return publicTenantBrandingInFlight;
}

/**
 * GET current tenant branding and cache raw `themeOptions` in sessionStorage.
 * Dispatches {@link TENANT_BRANDING_UPDATED_EVENT} on success.
 */
export async function fetchAndStoreTenantBrandingTheme(): Promise<unknown | undefined> {
  if (tenantBrandingMeInFlight) {
    return tenantBrandingMeInFlight;
  }

  tenantBrandingMeInFlight = (async () => {
    try {
      const url = `${getApiUrl()}/tenant-branding/me`;
      const { json } = await httpClient(url);
      return parseBrandingResponse(json);
    } finally {
      tenantBrandingMeInFlight = null;
    }
  })();

  return tenantBrandingMeInFlight;
}

/** Super admin on select-tenant: hostname branding only; otherwise authenticated tenant. */
export function shouldUsePublicTenantBranding(): boolean {
  const isSuperAdmin = localStorage.getItem("isSuperAdmin") === "1";
  const impersonatedTenantId = localStorage.getItem("impersonatedTenantId");
  return isSuperAdmin && !impersonatedTenantId;
}

/** Load hostname or authenticated tenant branding into sessionStorage. */
export async function fetchAndStoreTenantBranding(): Promise<unknown | undefined> {
  if (shouldUsePublicTenantBranding()) {
    return fetchAndStorePublicTenantBranding();
  }
  const theme = await fetchAndStoreTenantBrandingTheme().catch(() => undefined);
  if (theme !== undefined) return theme;
  return fetchAndStorePublicTenantBranding();
}

/**
 * Whether cached branding is enough to skip a network fetch.
 * Authenticated sessions also need `razaPrintPaperSize` (from `/tenant-branding/me`)
 * so raza hardcopy can choose A4 vs A5.
 */
export function isTenantBrandingCacheComplete(args: {
  isPublicBranding: boolean;
  hasTheme: boolean;
  hasAppTitle: boolean;
  hasPaperSize: boolean;
}): boolean {
  if (!args.hasTheme || !args.hasAppTitle) return false;
  if (!args.isPublicBranding && !args.hasPaperSize) return false;
  return true;
}

/**
 * Idempotent app-boot loader: returns cached branding or fetches once per tab.
 * Safe to call from multiple places (provider, PDF export, etc.).
 * Always re-applies `document.title` from cache so new tabs (receipt prints) get the
 * tenant name instead of the static `index.html` title.
 * Authenticated sessions also refresh when `razaPrintPaperSize` is missing so raza
 * hardcopy can read A4/A5 from `/tenant-branding/me`.
 */
export function ensureTenantBrandingLoaded(): Promise<unknown | undefined> {
  const hasTheme = hasStoredTenantBranding();
  const hasAppTitle = Boolean(sessionStorage.getItem(TENANT_BRANDING_APP_TITLE_STORAGE_KEY));
  const hasPaperSize = Boolean(
    sessionStorage.getItem(TENANT_BRANDING_RAZA_PRINT_PAPER_SIZE_STORAGE_KEY)
  );
  const cacheComplete = isTenantBrandingCacheComplete({
    isPublicBranding: shouldUsePublicTenantBranding(),
    hasTheme,
    hasAppTitle,
    hasPaperSize,
  });

  if (cacheComplete) {
    applyStoredDocumentTitle();
    return Promise.resolve(readStoredTenantBrandingTheme());
  }

  // Theme cached but no appTitle (pre-fix sessions): apply tenantName interim title,
  // then fetch so print PDF filenames get the real appTitle.
  if (hasTheme) {
    applyStoredDocumentTitle();
  }

  if (ensureTenantBrandingInFlight) {
    return ensureTenantBrandingInFlight;
  }
  ensureTenantBrandingInFlight = fetchAndStoreTenantBranding().finally(() => {
    ensureTenantBrandingInFlight = null;
  });
  return ensureTenantBrandingInFlight;
}

export function readStoredTenantBrandingTheme(): unknown {
  try {
    const s = sessionStorage.getItem(TENANT_BRANDING_THEME_STORAGE_KEY);
    if (!s) return undefined;
    return JSON.parse(s) as unknown;
  } catch {
    return undefined;
  }
}

function readStorageValue(key: string): string {
  try {
    return sessionStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

export function readStoredTenantName(): string {
  return readStorageValue(TENANT_BRANDING_NAME_STORAGE_KEY);
}

/** Raza hardcopy paper size from branding cache; defaults to A4 when unset. */
export function readStoredRazaPrintPaperSize(): RazaPrintPaperSize {
  return resolveRazaPrintPaperSize(
    readStorageValue(TENANT_BRANDING_RAZA_PRINT_PAPER_SIZE_STORAGE_KEY) || null
  );
}

export function readStoredTenantBrandingIdentity(): TenantBrandingIdentity {
  return {
    tenantName: readStoredTenantName(),
    trustName: readStorageValue(TENANT_BRANDING_TRUST_NAME_STORAGE_KEY),
    trustId: readStorageValue(TENANT_BRANDING_TRUST_ID_STORAGE_KEY),
    logoUrl: readStorageValue(TENANT_BRANDING_LOGO_URL_STORAGE_KEY),
  };
}
